/**
 * In-memory bridge between the in-place .flow editor inside
 * ObsidianVaultWindow and the popout VaultFlowWindow.
 *
 * The in-place editor calls `setActiveFlow({ path, raw })` when the
 * user clicks the popout button; the popout window subscribes via
 * `useActiveFlow()`. Edits do not propagate between in-place and
 * popout — both windows are independent editors against the same
 * starting bytes. This matches the C.7 Graph pattern.
 *
 * Persistence is the saveProvider's job; this module only fans out
 * the starting payload.
 */

import { useSyncExternalStore } from 'react';

interface ActiveFlow {
  path: string;
  raw: string;
}

let active: ActiveFlow | null = null;
const listeners = new Set<() => void>();

export function setActiveFlow(next: ActiveFlow | null): void {
  active = next;
  for (const l of listeners) l();
}

export function getActiveFlow(): ActiveFlow | null {
  return active;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): ActiveFlow | null {
  return active;
}

export function useActiveFlow(): ActiveFlow | null {
  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}
